/**
 * Agent command kernel types.
 *
 * Every AI consumer (in-app chat, MCP, Chrome bridge) talks to the same
 * object-params command surface. The window.__velostruct__ adapter preserves
 * legacy positional signatures so existing MCP servers keep working.
 */

export type Point = { x: number; y: number };

export type CommandError = { error: string };

export function isCommandError(result: unknown): result is CommandError {
  return (
    typeof result === 'object' &&
    result !== null &&
    'error' in result &&
    typeof (result as { error: unknown }).error === 'string'
  );
}

/** Methods the command kernel can execute. */
export const COMMAND_NAMES = [
  'getSummary', 'getNodes', 'findNode', 'getEdges',
  'addNode', 'updateNode', 'removeNode',
  'connectNodes', 'removeEdge', 'updateEdge',
  'addParticipant', 'removeParticipant', 'getParticipants',
  'addMessage', 'updateMessage', 'removeMessage', 'getMessages', 'insertMessage',
  'addFragment', 'removeFragment', 'getFragments', 'updateFragment',
  'importFromMermaid', 'exportToMermaid',
  'setPageDescription', 'getPageDetail',
  'autoLayoutFlow', 'reflowMessages', 'recalcFragments',
  'listPages', 'addPage', 'switchPage', 'linkNodeToPage',
  'frameNodes', 'createGroup', 'dissolveGroup',
  'clearDiagram', 'setDiagramType', 'getDiagramJson', 'setDiagramJson',
  'getAIContext',
  'exportToPng',
  'updateNodeMeta', 'getCatalog', 'updateCatalog', 'placeCatalogInstance',
  'createChildView', 'createC4Workspace',
] as const;

export type CommandName = (typeof COMMAND_NAMES)[number];

export const COMMAND_NAME_SET: ReadonlySet<string> = new Set(COMMAND_NAMES);

/**
 * How a method's arguments are packed when called positionally
 * (MCP / applyBatch / window.__velostruct__).
 *
 * - `positional`: argument names in call order
 * - `spreadLast`: last argument is an updates object, merged into the params
 * - `scalarKey`: a bare string/number is wrapped under this key
 */
export interface CommandArity {
  positional: string[];
  spreadLast?: boolean;
  scalarKey?: string;
}

export const COMMAND_ARITY: Record<string, CommandArity> = {
  getNodes: { positional: ['filter'] },
  findNode: { positional: ['labelOrId'], scalarKey: 'labelOrId' },
  addNode: { positional: ['type'] },
  updateNode: { positional: ['labelOrId', 'updates'], spreadLast: true, scalarKey: 'labelOrId' },
  removeNode: { positional: ['labelOrId'], scalarKey: 'labelOrId' },
  connectNodes: { positional: [] },
  removeEdge: { positional: [] },
  updateEdge: { positional: ['id', 'updates'], spreadLast: true, scalarKey: 'id' },
  addParticipant: { positional: [] },
  removeParticipant: { positional: ['labelOrId'], scalarKey: 'labelOrId' },
  addMessage: { positional: [] },
  updateMessage: { positional: ['id', 'updates'], spreadLast: true, scalarKey: 'id' },
  removeMessage: { positional: ['labelOrId'], scalarKey: 'labelOrId' },
  addFragment: { positional: [] },
  removeFragment: { positional: ['id'], scalarKey: 'id' },
  updateFragment: { positional: ['id', 'updates'], spreadLast: true, scalarKey: 'id' },
  insertMessage: { positional: [] },
  importFromMermaid: { positional: ['mmd'], scalarKey: 'mmd' },
  setPageDescription: { positional: ['nameOrId', 'description'], scalarKey: 'nameOrId' },
  getPageDetail: { positional: ['nameOrId'], scalarKey: 'nameOrId' },
  reflowMessages: { positional: ['options'] },
  recalcFragments: { positional: ['fragmentIds'] },
  addPage: { positional: ['name', 'diagramType'], scalarKey: 'name' },
  switchPage: { positional: ['nameOrId'], scalarKey: 'nameOrId' },
  linkNodeToPage: { positional: ['nodeLabelOrId', 'pageNameOrId'] },
  frameNodes: { positional: ['labelOrIds', 'label'] },
  createGroup: { positional: ['labelOrIds', 'label'] },
  dissolveGroup: { positional: ['labelOrId'], scalarKey: 'labelOrId' },
  clearDiagram: { positional: ['diagramType'], scalarKey: 'diagramType' },
  setDiagramType: { positional: ['type'], scalarKey: 'type' },
  setDiagramJson: { positional: ['json'], scalarKey: 'json' },
  exportToPng: { positional: ['options'] },
  getAIContext: { positional: ['options'] },
  updateNodeMeta: { positional: ['labelOrId', 'updates'], spreadLast: true, scalarKey: 'labelOrId' },
  updateCatalog: { positional: ['catalogId', 'patch'], spreadLast: true, scalarKey: 'catalogId' },
  placeCatalogInstance: { positional: ['catalogId', 'position'], scalarKey: 'catalogId' },
  createChildView: { positional: ['nodeLabelOrId', 'options'], spreadLast: true, scalarKey: 'nodeLabelOrId' },
  createC4Workspace: { positional: ['systemName'], scalarKey: 'systemName' },
};

export type CommandHandler = (params: Record<string, unknown>) => unknown;

export interface VelostructBridgeAPI {
  getSummary(): object;
  getNodes(filter?: { labelContains?: string; type?: string }): object[];
  findNode(labelOrId: string): object | null;
  addNode(params: { type: string; position?: Point; label?: string; color?: string }): { id: string } | CommandError;
  updateNode(labelOrId: string, updates: { newLabel?: string; position?: Point; color?: string }): { ok: true } | CommandError;
  removeNode(labelOrId: string): { removedId: string } | CommandError;
  connectNodes(params: { sourceNode: string; targetNode: string; sourceSocket?: number | string; targetSocket?: number | string; label?: string; animated?: boolean; color?: string; edgeType?: string }): { id: string } | CommandError;
  removeEdge(params: { id?: string; sourceNode?: string; targetNode?: string }): { removedId: string } | CommandError;
  updateEdge(id: string, updates: { label?: string; animated?: boolean; color?: string; strokeWidth?: number; dashStyle?: string; edgeType?: string }): { ok: true } | CommandError;
  addParticipant(params: { label: string; type?: string; color?: string; x?: number }): { id: string } | CommandError;
  removeParticipant(labelOrId: string): { ok: true } | CommandError;
  getParticipants(): object[];
  addMessage(params: { sourceParticipant: string; targetParticipant: string; label: string; type?: string; y?: number; color?: string }): { id: string } | CommandError;
  updateMessage(id: string, updates: { label?: string; type?: string; color?: string; strokeWidth?: number; dashStyle?: string }): { ok: true } | CommandError;
  removeMessage(labelOrId: string): { ok: true } | CommandError;
  getMessages(): object[];
  addFragment(params: { type: string; label: string; startY?: number; endY?: number; containsMessages?: string[]; participantIds?: string[]; altDividerY?: number; parentFragmentId?: string }): { id: string } | CommandError;
  removeFragment(id: string): { ok: true } | CommandError;
  getFragments(): object[];
  updateFragment(id: string, updates: { label?: string; startY?: number; endY?: number; altDividerY?: number; parentFragmentId?: string }): { ok: true } | CommandError;
  insertMessage(params: { after?: string; before?: string; sourceParticipant: string; targetParticipant: string; label: string; type?: string; color?: string; spacing?: number }): { id: string } | CommandError;
  importFromMermaid(mmd: string): Promise<{ ok: true; stats: object } | CommandError>;
  exportToMermaid(): { mermaid: string; type: string } | CommandError;
  setPageDescription(nameOrId: string, description: string): { ok: true } | CommandError;
  getPageDetail(nameOrId: string): object | CommandError;
  applyBatch(operations: Array<{ method: string; params?: unknown }>): Array<{ result?: unknown; error?: string }>;
  autoLayoutFlow(): Promise<{ ok: true; movedCount: number } | CommandError>;
  reflowMessages(options?: { startY?: number; spacing?: number }): { ok: true; messageCount: number } | CommandError;
  recalcFragments(fragmentIds?: string[]): { ok: true } | CommandError;
  getEdges(): object[];
  listPages(): object[];
  addPage(name: string, diagramType?: string): { id: string } | CommandError;
  switchPage(nameOrId: string): { ok: true } | CommandError;
  linkNodeToPage(nodeLabelOrId: string, pageNameOrId: string): { ok: true } | CommandError;
  frameNodes(labelOrIds: string[], label?: string): { frameId: string } | CommandError;
  createGroup(labelOrIds: string[], label?: string): { id: string } | CommandError;
  dissolveGroup(labelOrId: string): { ok: true } | CommandError;
  clearDiagram(diagramType?: string): { ok: true };
  setDiagramType(type: string): { ok: true };
  getDiagramJson(): string;
  setDiagramJson(json: string): { ok: true } | CommandError;
  getAIContext(options?: { userMessage?: string; maxTokens?: number }): object;
  exportToPng(options?: { allPages?: boolean }): Promise<{ pngData: string; fileName: string; pageCount?: number } | CommandError>;
  updateNodeMeta(labelOrId: string, updates: { description?: string; owner?: string; status?: string; links?: { label: string; url: string }[]; tooltip?: string }): { ok: true } | CommandError;
  getCatalog(): object[];
  updateCatalog(catalogId: string, patch: { name?: string; description?: string; owner?: string; status?: string; links?: { label: string; url: string }[]; type?: string; color?: string; tech?: string; c4Level?: string }): { ok: true } | CommandError;
  placeCatalogInstance(catalogId: string, position?: Point): { id: string } | CommandError;
  createChildView(nodeLabelOrId: string, options?: { name?: string; diagramType?: string }): { pageId: string } | CommandError;
  createC4Workspace(systemName?: string): { contextPageId: string; containersPageId: string; sequencePageId: string } | CommandError;
}

declare global {
  interface Window {
    __velostruct__?: VelostructBridgeAPI;
  }
}
